import {
    PaperPlaneRightIcon,
    ArrowClockwiseIcon,
    TimerIcon,
    BrainIcon,
    InfoIcon,
    QuestionIcon,
} from '@phosphor-icons/react'
import { ChatInput } from '../ChatInput/ChatInput'
import { ScoopSpinner, ScoopText, ScoopButton, ScoopTooltip, ScoopSelector } from '../../../../ui'
import styles from './ChatInputBox.module.scss'

export const ChatInputBox = ({
    // Position and layout
    isCenteredMode,
    drawerOpen,

    // Input state
    inputMessage,
    setInputMessage,
    handleSendMessage,

    // Connection and status
    isConnected,
    inputDisabled,
    isCreatingChat,

    // Inbox/dataset selection
    selectedInbox,
    workspaceMetadata,
    handleSelectInbox,

    // Analysis mode and toggles
    analysisMode,
    setAnalysisMode,
    showProcessingSteps,
    setShowProcessingSteps,
    showQueryDetails,
    setShowQueryDetails,

    // Demo/public mode
    isPublic,
    promptsRemaining,

    // Chat actions
    currentChat,
    aiStatus,
    error,
    handleDescribeDataset,
    handleResetContext,
}) => {
    return (
        <div
            className={`${styles.container} ${
                isCenteredMode
                    ? `${styles.centered} ${drawerOpen ? styles.drawerOpen : styles.drawerClosed}`
                    : `${styles.normal} ${drawerOpen ? styles.drawerOpen : ''}`
            }`}
        >
            <div className={styles.paper}>
                {/* Input Row - Will expand upwards */}
                <div
                    className={`${styles.inputRow} ${isPublic ? styles.noBorder : styles.withBorder}`}
                >
                    <div className={styles.inputWrapper}>
                        <ChatInput
                            placeholder={
                                isPublic && promptsRemaining === 0
                                    ? 'Daily prompt limit reached. Sign up for unlimited access!'
                                    : isCreatingChat
                                      ? 'Creating new chat...'
                                      : 'Type your prompt...'
                            }
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault()
                                    handleSendMessage()
                                }
                            }}
                            disabled={inputDisabled}
                        />
                    </div>
                    {isCreatingChat && (
                        <div className={styles.loadingIndicator}>
                            <ScoopSpinner size={24} />
                        </div>
                    )}
                    {/* Send button for demo/public mode */}
                    {isPublic && (
                        <ScoopButton
                            variant="text"
                            onClick={() => handleSendMessage()}
                            disabled={inputDisabled || !inputMessage.trim()}
                            className={styles.sendButton}
                        >
                            <PaperPlaneRightIcon
                                size={20}
                                color={
                                    inputDisabled || !inputMessage.trim()
                                        ? styles.colorDisabled
                                        : styles.colorPrimary
                                }
                            />
                        </ScoopButton>
                    )}
                </div>

                {/* Controls Row - Only show for non-demo/non-embedded mode */}
                {!isPublic && (
                    <div className={styles.controlsRow}>
                        <div className={styles.controlButtons}>
                            {/* Reset Context Button */}
                            <ScoopTooltip
                                title="Reset context - clear conversation memory while keeping message history"
                                placement="top"
                            >
                                <ScoopButton
                                    variant="text"
                                    size="xs"
                                    onClick={handleResetContext}
                                    disabled={!currentChat || !selectedInbox || !!aiStatus}
                                    className={`${styles.toggleButton} ${styles.inactive}`}
                                >
                                    <ArrowClockwiseIcon size={18} />
                                </ScoopButton>
                            </ScoopTooltip>

                            {/* Show Processing Steps Toggle */}
                            <ScoopTooltip
                                title={`${showProcessingSteps ? 'Hide' : 'Show'} processing steps`}
                                placement="top"
                            >
                                <ScoopButton
                                    variant="text"
                                    size="xs"
                                    onClick={() => setShowProcessingSteps(!showProcessingSteps)}
                                    className={`${styles.toggleButton} ${showProcessingSteps ? styles.active : styles.inactive}`}
                                >
                                    <TimerIcon
                                        size={18}
                                        weight={showProcessingSteps ? 'fill' : 'regular'}
                                    />
                                </ScoopButton>
                            </ScoopTooltip>

                            {/* Show Query Details Toggle */}
                            <ScoopTooltip
                                title={`${showQueryDetails ? 'Hide' : 'Show'} query details`}
                                placement="top"
                            >
                                <ScoopButton
                                    variant="text"
                                    size="xs"
                                    onClick={() => setShowQueryDetails(!showQueryDetails)}
                                    className={`${styles.toggleButton} ${showQueryDetails ? styles.active : styles.inactive}`}
                                >
                                    <InfoIcon
                                        size={18}
                                        weight={showQueryDetails ? 'fill' : 'regular'}
                                    />
                                </ScoopButton>
                            </ScoopTooltip>

                            {/* Deep Analysis Toggle */}
                            <ScoopTooltip
                                title={
                                    analysisMode === 'deep'
                                        ? 'Switch to quick mode: Fast responses with basic analysis'
                                        : 'Enable deep analysis: Comprehensive exploration (30+ seconds)'
                                }
                                placement="top"
                            >
                                <ScoopButton
                                    variant="text"
                                    size="xs"
                                    onClick={() =>
                                        setAnalysisMode(analysisMode === 'deep' ? 'quick' : 'deep')
                                    }
                                    disabled={!isConnected}
                                    className={`${styles.toggleButton} ${analysisMode === 'deep' ? styles.active : styles.inactive}`}
                                >
                                    <BrainIcon
                                        size={18}
                                        weight={analysisMode === 'deep' ? 'fill' : 'regular'}
                                    />
                                </ScoopButton>
                            </ScoopTooltip>

                            {/* Divider */}
                            <div className={styles.divider} />

                            {/* Connection Status */}
                            {!isConnected && (
                                <div className={styles.connectionStatus}>
                                    <ScoopSpinner size={20} />
                                    <ScoopText className={styles.connectionText}>
                                        {error ? 'Reconnecting...' : 'Connecting...'}
                                    </ScoopText>
                                </div>
                            )}

                            {/* Compact Dataset Selector with Description Button */}
                            {isConnected && (
                                <div className={styles.datasetSelectorContainer}>
                                    <ScoopSelector
                                        value={selectedInbox?.inboxID || ''}
                                        onChange={(e) => handleSelectInbox(e.target.value)}
                                        options={
                                            workspaceMetadata?.inboxes
                                                ?.filter((i) => i.tables?.length)
                                                .map((inbox) => ({
                                                    value: inbox.inboxID,
                                                    label: inbox.label || inbox.name,
                                                })) || []
                                        }
                                        size="xs"
                                        className={styles.datasetSelector}
                                        maxHeight={300}
                                        removeMargin
                                    />

                                    {/* Dataset Description Button - only in full experience mode */}
                                    {selectedInbox && !isPublic && (
                                        <ScoopTooltip title="Describe this dataset" placement="top">
                                            <ScoopButton
                                                variant="text"
                                                size="xs"
                                                onClick={handleDescribeDataset}
                                                disabled={!isConnected}
                                                className={styles.datasetDescButton}
                                            >
                                                <QuestionIcon size={18} />
                                            </ScoopButton>
                                        </ScoopTooltip>
                                    )}
                                </div>
                            )}
                        </div>
                        <ScoopButton
                            variant="text"
                            size="xs"
                            onClick={() => handleSendMessage()}
                            disabled={inputDisabled || !inputMessage.trim()}
                            className={styles.sendButtonFull}
                        >
                            <PaperPlaneRightIcon
                                size={18}
                                color={
                                    inputDisabled || !inputMessage.trim()
                                        ? styles.colorDisabled
                                        : styles.colorPrimary
                                }
                            />
                        </ScoopButton>
                    </div>
                )}
            </div>
        </div>
    )
}
